import type { ConversationListItem } from '@tandemone/types'
import { Link, useLocation } from 'react-router'

import { Skeleton } from '@/components/ui/skeleton'
import { useScopePrefix } from '@/domains/setup'
import { cn } from '@/lib/utils'

import { useConversations } from './use-conversations'

/** left pane of /messages. lists every conversation visible to the caller
 * with unread count + last message preview. clicking a row navigates to
 * /messages/<id> which renders the thread in the right pane.
 *
 * "TandemOne" rows always sort first when present (matches the participant
 * hydration order in the api). last-active first beyond that.
 */
export function ConversationList({ search }: { search?: string } = {}) {
  const query = useConversations({ search })

  if (query.isLoading) {
    return (
      <div className='flex flex-col gap-2 p-3'>
        <Skeleton className='h-16 w-full rounded-md' />
        <Skeleton className='h-16 w-full rounded-md' />
        <Skeleton className='h-16 w-full rounded-md' />
      </div>
    )
  }

  if (query.isError) {
    return (
      <div className='rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive'>
        Failed to load conversations. Try refreshing.
      </div>
    )
  }

  const items = query.data?.items ?? []
  if (items.length === 0) {
    const trimmed = search?.trim() ?? ''
    return (
      <div className='flex h-full flex-col items-center justify-center p-6 text-center'>
        <p className='text-[13px] text-ink-soft'>
          {trimmed
            ? `No conversations match "${trimmed}".`
            : 'No conversations yet.'}
        </p>
      </div>
    )
  }

  return (
    <div className='flex flex-col'>
      {items.map(item => (
        <ConversationRow key={item.id} item={item} />
      ))}
    </div>
  )
}

function ConversationRow({ item }: { item: ConversationListItem }) {
  const location = useLocation()
  const prefix = useScopePrefix()
  const href = `${prefix}/messages/${item.id}`
  const active = location.pathname === href
  const otherSides = item.participants
    .map(participant => participant.companyName)
    /** dedupe . a multi-company conversation might list the same name
     * twice if a company is a participant on both supplier and buyer side
     * in a future variant. for v1 with one participant row per company
     * this is a no-op.
     */
    .filter((name, idx, arr) => arr.indexOf(name) === idx)
    .join(' · ')

  const last = item.lastMessage
  return (
    <Link
      to={href}
      className={cn(
        'flex flex-col gap-1 border-b border-border px-3 py-3 transition-colors hover:bg-cream/40',
        active && 'bg-cream/60'
      )}
    >
      <div className='flex items-center gap-2'>
        <span className='line-clamp-1 flex-1 text-[13px] font-medium text-ink'>
          {item.subject}
        </span>
        {item.unreadCount > 0 ? (
          <span className='inline-flex min-w-[18px] items-center justify-center rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-semibold text-white'>
            {item.unreadCount}
          </span>
        ) : null}
      </div>
      <span className='line-clamp-1 text-[11px] text-ink-muted'>
        {otherSides}
      </span>
      {last ? (
        <span className='line-clamp-1 text-[12px] text-ink-soft'>
          <span className='text-ink-muted'>
            {last.senderUserName ?? 'TandemOne'}:
          </span>{' '}
          {stripHtml(last.body)}
        </span>
      ) : (
        <span className='text-[12px] italic text-ink-muted'>
          No messages yet.
        </span>
      )}
    </Link>
  )
}

/** the conversation list shows a one-line preview of the last message.
 * message bodies are sanitized html . strip the tags here so we don't
 * leak markup into the list (and so the line-clamp counts characters
 * the user actually sees).
 */
function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent?.trim() ?? ''
}
